import nodeReadline from 'node:readline'

import { SelectionAbortedError } from '../../errors'

// An arrow-key list picker built on node's own keypress events — no dependency, which matters for a CLI
// users install into their own projects.
//
// It needs BOTH streams to be terminals: stdin for raw-mode keypresses, stdout to move the cursor back up
// and redraw. When either is missing (a pipe, CI, an agent) the caller falls back to typed input.

export interface Choice {
  label: string
  /** Second line, dimmed. Optional. */
  description?: string
}

export interface KeyEvent {
  name?: string
  ctrl?: boolean
}

// Kept under its historical name while this module still lives here; the class itself is now part of the
// single hierarchy in `errors.ts`.
export { SelectionAbortedError as SelectionAborted } from '../../errors'

export function canRenderList(): boolean {
  return Boolean(process.stdin.isTTY && process.stdout.isTTY)
}

/**
 * Where a keypress moves the cursor. Split out from the rendering so the navigation rules are testable
 * without a terminal: wrap-around at both ends, vim keys, and the number shortcuts.
 *
 * Returns the next index, or an action.
 */
export function resolveKey(
  input: string | undefined,
  key: KeyEvent,
  current: number,
  total: number
): { index: number } | { action: 'select' | 'abort' } {
  if (key.ctrl && key.name === 'c') {
    return { action: 'abort' }
  }

  switch (key.name) {
    case 'up':
    case 'k':
      return { index: (current - 1 + total) % total }

    case 'down':
    case 'j':
    case 'tab':
      return { index: (current + 1) % total }

    case 'home':
      return { index: 0 }

    case 'end':
      return { index: total - 1 }

    case 'return':
    case 'enter':
    case 'space':
      return { action: 'select' }

    case 'escape':
    case 'q':
      return { action: 'abort' }

    default:
      break
  }

  // Number keys jump straight to an entry — faster than arrowing for anyone who already knows the list.
  const digit = Number(input)

  if (input && Number.isInteger(digit) && digit >= 1 && digit <= total) {
    return { index: digit - 1 }
  }

  return { index: current }
}

const HIDE_CURSOR = '\x1b[?25l'
const SHOW_CURSOR = '\x1b[?25h'

interface Styling {
  dim: string
  bold: string
  reset: string
}

function styling(): Styling {
  return process.env.NO_COLOR
    ? { dim: '', bold: '', reset: '' }
    : { dim: '\x1b[2m', bold: '\x1b[1m', reset: '\x1b[0m' }
}

/** Truncate to the terminal width so nothing wraps — a wrapped line would break the redraw line count. */
function fit(line: string, plainLength: number): string {
  const width = (process.stdout.columns || 80) - 1

  return plainLength <= width ? line : `${ line.slice(0, line.length - (plainLength - width) - 1) }…`
}

function frame(choices: Choice[], selected: number): string[] {
  const { dim, bold, reset } = styling()
  const lines: string[] = []

  for (const [index, choice] of choices.entries()) {
    const active = index === selected
    const pointer = active ? '❯' : ' '
    const label = active ? `${ bold }${ choice.label }${ reset }` : choice.label

    lines.push(fit(`${ pointer } ${ label }`, `${ pointer } ${ choice.label }`.length))

    if (choice.description) {
      lines.push(fit(`  ${ dim }${ choice.description }${ reset }`, `  ${ choice.description }`.length))
    }
  }

  lines.push('')
  lines.push(fit(`${ dim }  ↑/↓ to move · enter to select · esc to cancel${ reset }`,
    '  ↑/↓ to move · enter to select · esc to cancel'.length))

  return lines
}

/**
 * Render `choices` and let the user move through them with the arrow keys. Resolves with the chosen index.
 *
 * Rejects with SelectionAborted on Ctrl-C or Escape, so the caller decides the exit path rather than this
 * helper killing the process.
 */
export function selectFromList(title: string, choices: Choice[], startAt = 0): Promise<number> {
  const { stdin, stdout } = process

  return new Promise((resolve, reject) => {
    let selected = Math.min(Math.max(startAt, 0), choices.length - 1)
    let painted = 0

    const draw = (): void => {
      if (painted > 0) {
        nodeReadline.moveCursor(stdout, 0, -painted)
        nodeReadline.clearScreenDown(stdout)
      }

      const lines = frame(choices, selected)

      stdout.write(`${ lines.join('\n') }\n`)
      painted = lines.length
    }

    // Every exit path runs this. Leaving raw mode on, or the cursor hidden, would wreck the prompts that
    // follow — the id and name questions use a normal readline interface on the same stdin.
    const cleanup = (): void => {
      stdin.off('keypress', onKeypress)

      if (stdin.isTTY) {
        stdin.setRawMode(false)
      }

      stdin.pause()
      stdout.write(SHOW_CURSOR)
    }

    function onKeypress(input: string | undefined, key: KeyEvent = {}): void {
      const outcome = resolveKey(input, key, selected, choices.length)

      if ('action' in outcome) {
        cleanup()

        if (outcome.action === 'abort') {
          reject(new SelectionAbortedError('Cancelled.'))

          return
        }

        resolve(selected)

        return
      }

      if (outcome.index !== selected) {
        selected = outcome.index
        draw()
      }
    }

    stdout.write(`\n${ title }\n\n`)
    stdout.write(HIDE_CURSOR)

    nodeReadline.emitKeypressEvents(stdin)
    stdin.setRawMode(true)
    stdin.resume()
    stdin.on('keypress', onKeypress)

    draw()
  })
}
